"use client";

import { IGridComponent } from "@faire/web-api/indigofair/data/IGridComponent";
import { IGridStyle } from "@faire/web-api/indigofair/data/IGridStyle";
import { ITile } from "@faire/web-api/indigofair/data/ITile";
import { Spacing } from "@faire/web-api/indigofair/data/Spacing";
import { Column } from "@faire/web/slate/Layout";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { WhenVisibilityIsImminent } from "@faire/web/ui/VisibilitySensor/WhenVisibilityIsImminent";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import classNames from "classnames";
import chunk from "lodash/chunk";
import React, { type JSX, PropsWithChildren, useMemo } from "react";

import {
  convertServerResponsivePropertyToClientResponsiveProperty,
  getResponsiveStylingClassNames,
} from "@faire/retailer/components/SDUI/__internal__/Styling/responsiveValues";
import { ResponsiveSpacingPixels } from "@faire/retailer/components/SDUI/__internal__/Styling/spacing";
import { ComponentLoggingEventParams } from "@faire/retailer/components/SDUI/__internal__/types";
import {
  BrandTileRowPlaceholder,
  ProductTileRowPlaceholder,
} from "@faire/retailer/components/SDUI/ComponentRenderers/Grid/__internal__/GridRowPlaceholder";
import { SDUIGridHeaderRow } from "@faire/retailer/components/SDUI/ComponentRenderers/Grid/__internal__/SDUIGridHeaderRow";
import { SDUIGridRow } from "@faire/retailer/components/SDUI/ComponentRenderers/Grid/__internal__/SDUIGridRow";
import * as gridStyles from "@faire/retailer/components/SDUI/ComponentRenderers/Grid/__internal__/styles.css";
import {
  getFixedColumnGridStyleVariables,
  getGridRowGapStyleVariable,
} from "@faire/retailer/components/SDUI/ComponentRenderers/Grid/__internal__/utils/gridStylingUtils";

const FALLBACK_NUMBER_OF_TILES_PER_ROW = 2;

export const SDUIGridFixedColumnLayoutBase: React.FC<{
  gridComponent: IGridComponent;
  loggingData: ComponentLoggingEventParams;
  isRequiredForInteractivity?: boolean;
}> = ({ gridComponent, isRequiredForInteractivity, loggingData }) => {
  const {
    column_layout = IGridComponent.IColumnLayout.build(),
    header_background_icon,
    log_tile_batch_view,
    responsive_header_background_icon,
    responsive_title,
    style = IGridStyle.build(),
    tiles,
    title,
  } = gridComponent;

  const {
    column_spacing = Spacing.SPACING_NONE,
    row_spacing = Spacing.SPACING_NONE,
  } = style;

  const responsiveNumberOfTilesPerRow =
    convertServerResponsivePropertyToClientResponsiveProperty(
      column_layout.fixed?.num_columns ?? FALLBACK_NUMBER_OF_TILES_PER_ROW,
      (col) => col,
      column_layout.fixed?.responsive_num_columns
    );

  const currentNumberOfTilesPerRow = useResponsiveValue(
    responsiveNumberOfTilesPerRow
  );

  const tileChunks = useMemo(() => {
    return chunk(tiles, currentNumberOfTilesPerRow);
  }, [currentNumberOfTilesPerRow, tiles]);

  return (
    <Column className={gridStyles.SDUIGridWrapper}>
      <SDUIGridHeaderRow
        title={title}
        responsiveTitle={responsive_title}
        backgroundIcon={header_background_icon}
        responsiveBackgroundIcon={responsive_header_background_icon}
        loggingData={loggingData}
      />

      {tileChunks.map((tiles, index) => {
        return (
          <MaybeWhenVisibilityIsImminent
            key={tiles[0]?.logging_data?.instance_identifier ?? index}
            forceVisibility={index === 0 && isRequiredForInteractivity}
            rowPlaceholder={getRowPlaceholder(
              tiles[0],
              assignInlineVars(
                getGridRowGapStyleVariable(
                  index === 0 ? undefined : row_spacing
                )
              )
            )}
          >
            <SDUIGridRow
              tiles={tiles}
              loggingData={loggingData}
              className={classNames(
                gridStyles.gridTileContainer,
                getResponsiveStylingClassNames(
                  responsiveNumberOfTilesPerRow,
                  ResponsiveGridTemplateColumnsClassNames
                )
              )}
              componentStyle={assignInlineVars(
                getFixedColumnGridStyleVariables(
                  responsiveNumberOfTilesPerRow,
                  column_spacing,
                  index === 0 ? undefined : row_spacing
                )
              )}
              logTileBatchView={log_tile_batch_view}
            />
          </MaybeWhenVisibilityIsImminent>
        );
      })}
    </Column>
  );
};

const ResponsiveGridTemplateColumnsClassNames: Partial<
  Record<keyof ResponsiveSpacingPixels, string>
> = {
  tabletAndAbove: gridStyles.tabletAndAboveGridTemplateColumns,
  desktopAndAbove: gridStyles.desktopAndAboveGridTemplateColumns,
  xLargeAndAbove: gridStyles.xLargeAndAboveGridTemplateColumns,
};

const getRowPlaceholder = (
  tile?: ITile,
  placeholderStyles?: React.CSSProperties
) => {
  if (!tile) {
    return undefined;
  }

  if (tile.brand_tile || tile.brand_tile_with_action) {
    return <BrandTileRowPlaceholder placeholderStyles={placeholderStyles} />;
  } else if (tile.product_tile || tile.product_with_brand_info_tile) {
    return <ProductTileRowPlaceholder placeholderStyles={placeholderStyles} />;
  }

  return undefined;
};

const MaybeWhenVisibilityIsImminent: React.FC<
  PropsWithChildren & {
    forceVisibility?: boolean;
    rowPlaceholder?: JSX.Element;
  }
> = ({ forceVisibility, rowPlaceholder, children }) => {
  if (!forceVisibility && rowPlaceholder) {
    return (
      <WhenVisibilityIsImminent
        customizePlaceholder={rowPlaceholder}
        shouldRunNormallyInTest
      >
        {children}
      </WhenVisibilityIsImminent>
    );
  }

  return children;
};
